import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiConfig, BLOB_BASE_URL } from '../auth';
import { resolveUrl } from '../utils/url';
import { toCmsContents } from '../utils/sceneContents';
import { alignAnchorToWorld, alignPlacementToWorld, animateCameraPullback } from 'trace-viewer/scene';
import {
    findRootContent,
    loadCmsScene,
    buildCmsSceneInput,
    isSceneAnchored,
    effectiveScenePlacements,
    resolveImageAnchorUrl,
    trackedLocalizersFromPlacements,
    resolveLocalizerSelection,
    Scenes,
} from 'trace-viewer/cms';
import { LocalizerChoice } from '../components/LocalizerChoice';
import type { CmsAnchoredSceneSource, ScenePlacement } from 'trace-viewer/cms';
import type { SceneResponseDto } from 'trace-viewer/cms';

// Import trace-viewer to register the custom element + pick up HTMLElementTagNameMap augmentation
import type { TraceViewer } from 'trace-viewer';
import 'trace-viewer';

interface Props {
    scene: SceneResponseDto;
    onBack: () => void;
}

type PreloadState = 'loading' | 'ready' | 'error';

/**
 * Combined landing + scene view. Preloads the scene in a hidden <trace-viewer>
 * while showing a landing page. On tap, the viewer goes full-screen (and enters
 * XR if supported). The <trace-viewer> element never moves in the DOM, avoiding
 * disconnectedCallback disposal.
 */
/**
 * True on phones/tablets where WebXR is unavailable (e.g. iOS Safari).
 * In this case we bypass the preload and link out to the native App Clip
 * via `https://trce.ch/scenes/{shortCode}` instead.
 */
function useMobileWithoutXR(): boolean | null {
    const [result, setResult] = useState<boolean | null>(null);
    useEffect(() => {
        // iPadOS 13+ Safari reports a desktop "Macintosh" UA with no "Mobi"/"iPad"
        // token, so UA sniffing alone misses iPads. Detect them via touch points:
        // real Macs report maxTouchPoints === 0.
        const isIPad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        const isMobile = /Mobi|Android/i.test(navigator.userAgent) || isIPad;
        if (!isMobile) {
            setResult(false);
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const xr = (navigator as any).xr;
        if (!xr?.isSessionSupported) {
            setResult(true);
            return;
        }
        let cancelled = false;
        xr.isSessionSupported('immersive-ar')
            .then((supported: boolean) => {
                if (!cancelled) setResult(!supported);
            })
            .catch(() => {
                if (!cancelled) setResult(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);
    return result;
}

/** Marker width fallback when the scene record doesn't specify one. */
const DEFAULT_MARKER_WIDTH_METERS = 0.15;

export function SceneLanding({ scene, onBack }: Props) {
    const { token } = useAuth();
    const [localizers, setLocalizers] = useState<ScenePlacement[] | null>(null);
    const [selectedPlacementId, setSelectedPlacementId] = useState<string | undefined>();
    const selection = localizers === null ? null : resolveLocalizerSelection(localizers, selectedPlacementId);
    const selected = selection?.status === 'selected' ? selection.session.selected : null;

    const viewerContainerRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<TraceViewer | null>(null);
    const imageWrapRef = useRef<HTMLDivElement>(null);
    const [preloadState, setPreloadState] = useState<PreloadState>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    /** Download progress 0–1, or null if Content-Length unavailable */
    const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
    const [xrSupported, setXrSupported] = useState(false);
    const imageUrl = (xrSupported ? selected?.localizer?.imageUrl : null) ?? resolveUrl(scene.imageUrl);
    const previewRef = useRef<(() => void) | null>(null);
    const [playing, setPlaying] = useState(false);
    /**
     * null = still detecting, true = mobile + no WebXR (use App link fallback),
     * false = desktop or XR-capable mobile (use normal preload flow).
     */
    const mobileNoXR = useMobileWithoutXR();
    const appLink = scene.shortCode ? `https://trce.ch/scenes/${scene.shortCode}` : null;
    /**
     * Whether the scene is image-marker anchored, per the shared accessor
     * predicate. NOT `scene.imageUrl` — the backend can't clear that on
     * un-anchor, so a lingering URL must not be honored (ZIP_FRAME_CONTRACT).
     */
    const anchored = isSceneAnchored(scene) || !!buildCmsSceneInput(scene, { includeDisabledPrimary: true }).anchorPlacement;
    /** True when alignAnchorToWorld was used (desktop image-anchor fallback). */
    const desktopAnchorRef = useRef(false);

    /** Measure the rendered image-wrap size as a fraction of the smaller viewport dimension. */
    const getImageScreenFraction = () => {
        const el = imageWrapRef.current;
        if (!el) return undefined;
        const size = Math.min(el.offsetWidth, el.offsetHeight);
        const vmin = Math.min(window.innerWidth, window.innerHeight);
        return vmin > 0 ? size / vmin : undefined;
    };

    // Preload the scene in a hidden trace-viewer
    useEffect(() => {
        if (!token || !scene.id) return;
        // Skip preload on mobile devices without WebXR — they get an App Clip link instead.
        if (mobileNoXR !== false) return;

        const container = viewerContainerRef.current;
        if (!container) return;

        let cancelled = false;
        let viewer: TraceViewer | null = null;

        const preload = async () => {
            // Defer viewer creation one frame so the landing page fully
            // paints before WebGL context creation blocks the main thread.
            await new Promise<void>((r) => requestAnimationFrame(() => r()));
            if (cancelled) return;

            viewer = document.createElement('trace-viewer');
            viewer.setAttribute('ground-shadow', '');
            // XRManager's DOM overlay carries the Exit AR button: the only way
            // out of AR where there's no system back (the iOS App Clip).
            viewer.setAttribute('xr', '');
            viewer.setAttribute('backgroundcolor', '#f6f6f6');
            viewer.setAttribute('logger', 'log'); // milestones only; 'enabled' for per-object debug

            viewer.style.pointerEvents = 'none';

            container.appendChild(viewer);
            viewerRef.current = viewer;

            try {
                // Fetch scene contents in parallel with viewer initialisation
                const config = apiConfig(token);
                const [, contentsRes] = await Promise.all([
                    viewer.whenReady(),
                    new Scenes(config).contentsList(scene.id!),
                ]);

                if (cancelled) return;

                // Disable play mode before loading so videos/animations don't auto-start
                viewer.setPlayMode(false);

                // Narrow the auto-generated DTO to the CmsContentInput shape
                // the trace-viewer/cms helpers expect. `url` is passed
                // through verbatim — for a folder scene it addresses the
                // document inside `trace-content/scenes/{id}/`, which is also
                // the base its relative `assets/` URIs resolve against.
                const contents = toCmsContents(contentsRes.data?.contents);

                if (!findRootContent(contents)) {
                    setErrorMessage('No content');
                    setPreloadState('error');
                    return;
                }

                // Shared CMS load path (same as sandbox/studio): opens
                // RootContent, applies the legacy `.zip` anchor-frame
                // conversion, and attaches the ImageAnchor preview with the
                // accessor's authored orientation + pitch. Alignment is
                // skipped here because only the desktop fallback below wants
                // it — XR poses the scene root via the reference-space
                // offset, which must not compose with the preview rotation.
                const anchorInput = buildCmsSceneInput(scene, { includeDisabledPrimary: true });
                const result = await loadCmsScene(viewer, anchorInput, contents, BLOB_BASE_URL, {
                    deferRendering: true,
                    skipAnchorAlignment: true,
                    defaultAnchorWidthMeters: DEFAULT_MARKER_WIDTH_METERS,
                    onProgress: (progress) => {
                        if (cancelled) return;
                        setDownloadProgress(progress);
                    },
                });

                if (cancelled) return;

                if (!result.rootLoaded) {
                    // Root row exists (checked above) but failed to open.
                    setErrorMessage('Failed to load scene. Please try again.');
                    setPreloadState('error');
                    return;
                }

                // Set up XR alignment / desktop framing if applicable
                const placements = effectiveScenePlacements(scene as CmsAnchoredSceneSource);
                const resolved = placements === null ? null : trackedLocalizersFromPlacements(placements, BLOB_BASE_URL, result.anchorPlacement);
                setLocalizers(resolved);
                setSelectedPlacementId(undefined);
                const supported = await viewer.checkXRSupport();
                if (cancelled) return;
                setXrSupported(supported);
                if (result.anchorShown && anchorInput.imageUrl) {
                    const fullImageUrl = resolveImageAnchorUrl(BLOB_BASE_URL, anchorInput.imageUrl);
                    const widthMeters = anchorInput.imageWidth ?? DEFAULT_MARKER_WIDTH_METERS;
                    viewer.setImageAlignment(fullImageUrl, widthMeters);
                    const previewViewer = viewer;
                    previewRef.current = () => {
                        const fraction = getImageScreenFraction();
                        if (result.anchorPlacement) alignPlacementToWorld(previewViewer, result.anchorPlacement.pose, widthMeters, fraction);
                        else alignAnchorToWorld(previewViewer, widthMeters, fraction);
                        desktopAnchorRef.current = true;
                    };
                    if (!supported) previewRef.current();
                }

                if (cancelled) return;
                setPreloadState('ready');
            } catch (err) {
                if (cancelled) return;
                console.error('[SceneLanding] Preload failed:', err);
                const msg = err instanceof Error ? err.message : '';
                if (/webgl|gl context/i.test(msg)) {
                    setErrorMessage('Your browser does not support WebGL, which is required to view 3D scenes.');
                } else {
                    setErrorMessage('Failed to load scene. Please try again.');
                }
                setPreloadState('error');
            }
        };

        preload();

        return () => {
            cancelled = true;
            if (viewer) viewer.remove();
            viewerRef.current = null;
        };
    }, [token, scene.id, mobileNoXR]);

    // Track whether this session was launched in XR-only mode (image anchor + XR)
    const enteredXRRef = useRef(false);

    // When an XR-only session ends (e.g. Android back button), return to the
    // landing page instead of falling through to the flat embedded renderer.
    useEffect(() => {
        const viewer = viewerRef.current;
        if (!playing || !viewer) return;
        viewer.toggleAttribute('data-in-ar', viewer.xrState === 'active');

        const onXRStateChanged = (e: Event) => {
            const detail = (e as CustomEvent<{ isActive: boolean; state: string }>).detail;
            viewer.toggleAttribute('data-in-ar', detail.isActive);
            if (enteredXRRef.current && !detail.isActive && detail.state === 'available') {
                viewer.setPlayMode(false);
                setPlaying(false);
                enteredXRRef.current = false;
            }
        };

        viewer.addEventListener('trace-viewer:xr-state-changed', onXRStateChanged);
        return () => viewer.removeEventListener('trace-viewer:xr-state-changed', onXRStateChanged);
    }, [playing]);

    const handlePlay = (previewOnly = false) => {
        if (preloadState !== 'ready' || !viewerRef.current) return;

        const viewer = viewerRef.current;
        if (!previewOnly && xrSupported) {
            try {
                if (localizers !== null && selection?.status !== 'selected')
                    throw new Error(selection?.reason ?? 'Choose a marker before entering AR.');
                viewer.setLocalizers(localizers, selected?.id);
            } catch (e) {
                setErrorMessage(e instanceof Error ? e.message : 'Unable to select this marker.');
                return;
            }
        }

        if (previewOnly) previewRef.current?.();

        // Re-enable pointer events (disabled during preload)
        viewer.style.pointerEvents = '';

        // Frame the camera for non-anchor scenes (anchor scenes are already
        // positioned by loadCmsScene / alignAnchorToWorld)
        if (!anchored) {
            viewer.frameScene();
        }

        // For desktop anchor scenes the pullback animates from dark → light,
        // so set the initial clear color to dark before the first rendered frame.
        if (desktopAnchorRef.current) {
            viewer.setBackgroundColor('#111111');
        }

        // Activate deferred rendering
        viewer.startRendering();

        // Start play mode — triggers videos, animations, interactions
        viewer.setPlayMode(true);
        const enteringXR = !previewOnly && xrSupported && (anchored || !!selected) &&
            viewer.xrState === 'available';

        if (enteringXR) {
            // Hide scene content before revealing the viewer so the flat renderer
            // never flashes the scene. XRManager will re-show it after alignment.
            const sceneRoot = viewer.getSceneRoot();
            if (sceneRoot) {
                sceneRoot.transform.setEnabled(false);
            }
            // WebXR: Android Chrome, or the iOS App Clip's native host.
            // Enter XR synchronously within this click handler so the browser
            // accepts it as a user gesture. enterXR() calls requestSession()
            // internally which must happen in the gesture's microtask.
            enteredXRRef.current = true;
            viewer.enterXR().catch((e: unknown) => {
                console.warn('[SceneLanding] enterXR failed:', e);
                setErrorMessage('AR could not start. Please try again.');
                setPlaying(false);
                enteredXRRef.current = false;
                // Re-show content on failure
                const sceneRoot = viewer.getSceneRoot();
                if (sceneRoot) {
                    sceneRoot.transform.setEnabled(true);
                }
            });
        }

        // Reveal the viewer full-screen.
        setPlaying(true);
        requestAnimationFrame(() => {
            viewer.getEngine?.()?.resize?.();

            // For desktop image-anchor scenes, pull the camera back after the
            // initial matched view so the user sees the 3D scene around the marker.
            if (desktopAnchorRef.current) {
                animateCameraPullback(viewer, '#111111', '#f6f6f6');
            }
        });
    };

    const buttonLabel = (() => {
        if (mobileNoXR === true) {
            return appLink ? 'Open in App' : 'Not supported on this device';
        }
        if (mobileNoXR === null) return 'Loading...';
        if (preloadState === 'loading') return 'Loading...';
        if (preloadState === 'error') return errorMessage ?? 'Load failed';
        if (xrSupported && (anchored || !!selected)) return 'View in AR';
        return 'Open Scene';
    })();

    return (
        <div className="scene-viewer">
            {/* Landing overlay — hidden once playing */}
            {!playing && (
                <div className="landing">
                    <button className="landing-back" onClick={onBack} aria-label="Back to experiences">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                    </button>

                    {/* Image centered on screen */}
                    <div ref={imageWrapRef} className={`landing-image-wrap${!imageUrl ? ' placeholder' : ''}`}>
                        {imageUrl ? (
                            <img
                                className="landing-image"
                                src={imageUrl}
                                alt={scene.name ?? 'Scene image'}
                                draggable={false}
                            />
                        ) : (
                            <div className="landing-image-skeleton" />
                        )}
                        {mobileNoXR !== true && (
                            <div className="landing-progress" style={{ visibility: preloadState === 'loading' ? 'visible' : 'hidden' }}>
                                <div
                                    className={`landing-progress-fill${downloadProgress === null ? ' indeterminate' : ''}`}
                                    style={downloadProgress !== null ? { width: `${Math.round(downloadProgress * 100)}%` } : undefined}
                                />
                            </div>
                        )}
                    </div>

                    {/* Title, status, and button below the image */}
                    <div className="landing-footer">
                        <h1 className="landing-title">{scene.name ?? 'Untitled Scene'}</h1>

                        {scene.description && (
                            <p className="landing-description">{scene.description}</p>
                        )}

                        {errorMessage && (
                            <p className="landing-error">{errorMessage}</p>
                        )}

                        {localizers !== null && xrSupported && (
                            <LocalizerChoice placements={localizers} selectedId={selected?.id ?? ''}
                                onChange={(id) => { setSelectedPlacementId(id); setErrorMessage(null); }} />
                        )}
                        {selection && selection.status !== 'selected' && xrSupported && (
                            <>
                                <p role="status">{selection.reason}</p>
                                <button onClick={() => handlePlay(true)}>Open 3D preview</button>
                            </>
                        )}
                        {mobileNoXR === true && appLink ? (
                            <a
                                className="landing-play"
                                href={appLink}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M8 5v14l11-7z"/>
                                </svg>
                                <span>{buttonLabel}</span>
                            </a>
                        ) : (
                            <button
                                className="landing-play"
                                onClick={() => handlePlay()}
                                disabled={mobileNoXR === true || preloadState !== 'ready' || (xrSupported && selection !== null && selection.status !== 'selected')}
                            >
                                {mobileNoXR === false && preloadState === 'ready' && (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z"/>
                                    </svg>
                                )}
                                <span>{buttonLabel}</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Scene chrome — shown once playing */}
            {playing && (
                <>
                    <button className="back-button" onClick={onBack}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                    </button>
                    <div className="scene-title-bar">
                        <span className="scene-title-text">{scene.name ?? 'Scene'}</span>
                    </div>
                </>
            )}

            {/* Viewer container — always mounted, starts hidden, becomes full-screen */}
            <div
                ref={viewerContainerRef}
                className={playing ? 'viewer-container' : 'landing-preload'}
            />
        </div>
    );
}
